import React, { useMemo, useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { formatPeriodLabel, formatCompactCurrency } from '../utils/formatters';
import ChartLegend from './ChartLegend';
import CustomYAxis from './CustomYAxis';
import './CashFlowChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const outlierFadePlugin = {
    id: 'outlierFade',
    afterDatasetsDraw(chart) {
        const { ctx, chartArea, scales: { y } } = chart;
        if (!y || !chartArea) return;

        const yMax = y.max;
        if (yMax == null) return;

        chart.data.datasets.forEach((dataset, datasetIndex) => {
            if (dataset.type !== 'bar') return;

            const meta = chart.getDatasetMeta(datasetIndex);
            meta.data.forEach((bar, index) => {
                const rawValue = dataset.data[index];
                if (rawValue == null || rawValue <= yMax) return;

                const fadeHeight = 80;
                const barLeft = bar.x - bar.width / 2;
                const fadeTop = chartArea.top;

                ctx.save();

                // White overlay: transparent at bottom → 80% white at top.
                // The bar (fill, border, hover state) renders normally underneath;
                // this just progressively washes it out toward the top while
                // letting ~20% of color and the cash-balance line peek through.
                const grad = ctx.createLinearGradient(0, fadeTop + fadeHeight, 0, fadeTop);
                grad.addColorStop(0, 'rgba(255, 255, 255, 0)');
                grad.addColorStop(1, 'rgba(255, 255, 255, 1)');
                ctx.fillStyle = grad;
                ctx.fillRect(barLeft, fadeTop, bar.width, fadeHeight);

                // Value label so the actual number is visible
                const label = formatCompactCurrency(rawValue);
                ctx.font = 'bold 9px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
                ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'top';
                ctx.fillText(label, bar.x, fadeTop + 4);

                ctx.restore();
            });
        });
    }
};

/**
 * Cash Flow Chart Component
 * Displays inflows, outflows, and ending cash balance
 */
const CashFlowChart = ({ components, visiblePeriods, granularity, gridTemplateColumns, actualPeriodCount = 0 }) => {
    const chartRef = useRef(null);
    
    // Get chart max height from CSS variable and calculate with window height
    const chartHeight = useMemo(() => {
        try {
            const raw = getComputedStyle(document.documentElement)
                .getPropertyValue('--chart-max-height')
                .trim();
            const maxHeight = parseInt(raw.replace('px', ''));
            return Number.isFinite(maxHeight) ? Math.min(window.innerHeight * 0.4, maxHeight) : 300;
        } catch (_) {
            return 300;
        }
    }, []);
    
    
    // Read CSS variable for label column width so we can force the y-axis to match it
    const labelColumnWidthPx = useMemo(() => {
        try {
            const raw = getComputedStyle(document.documentElement)
                .getPropertyValue('--label-column-width')
                .trim();
            const parsed = parseInt(raw.replace('px', ''));
            return Number.isFinite(parsed) ? parsed : 280;
        } catch (_) {
            return 280;
        }
    }, []);
    // Generate chart data from cashflow_summary component
    const chartData = useMemo(() => {
        // If no components or cashflow data, return empty structure
        if (!components || components.length === 0) {
            const labels = visiblePeriods?.map(p => formatPeriodLabel(p, granularity)) || [];
            return {
                labels,
                datasets: [
                    {
                        label: 'Cash Inflows',
                        data: Array(labels.length).fill(0),
                        backgroundColor: Array(labels.length).fill('rgba(17, 140, 79, 0.4)'),
                        borderColor: 'transparent',
                        borderWidth: 0,
                        order: 2,
                        type: 'bar',
                    },
                    {
                        label: 'Cash Outflows',
                        data: Array(labels.length).fill(0),
                        backgroundColor: Array(labels.length).fill('rgba(255, 99, 99, 0.4)'),
                        borderColor: 'transparent',
                        borderWidth: 0,
                        order: 2,
                        type: 'bar',
                    },
                    {
                        label: 'Ending Cash Balance',
                        data: Array(labels.length).fill(0),
                        backgroundColor: 'transparent',
                        borderColor: 'rgb(74, 144, 226)',
                        borderWidth: 2,
                        type: 'line',
                        pointRadius: 4,
                        pointBackgroundColor: 'rgb(74, 144, 226)',
                        pointBorderWidth: 2,
                        pointBorderColor: 'white',
                        order: 1,
                        tension: 0.1,
                    }
                ]
            };
        }
        
        // Find summary component
        const cashflowComponent = components.find(c => c.id === 'summary');
        if (!cashflowComponent?.view?.children) {
            const labels = visiblePeriods?.map(p => formatPeriodLabel(p, granularity)) || [];
            return {
                labels,
                datasets: [
                    {
                        label: 'Cash Inflows',
                        data: Array(labels.length).fill(0),
                        backgroundColor: Array(labels.length).fill('rgba(17, 140, 79, 0.4)'),
                        borderColor: 'transparent',
                        borderWidth: 0,
                        order: 2,
                        type: 'bar',
                    },
                    {
                        label: 'Cash Outflows',
                        data: Array(labels.length).fill(0),
                        backgroundColor: Array(labels.length).fill('rgba(255, 99, 99, 0.4)'),
                        borderColor: 'transparent',
                        borderWidth: 0,
                        order: 2,
                        type: 'bar',
                    },
                    {
                        label: 'Ending Cash Balance',
                        data: Array(labels.length).fill(0),
                        backgroundColor: 'transparent',
                        borderColor: 'rgb(74, 144, 226)',
                        borderWidth: 2,
                        type: 'line',
                        pointRadius: 4,
                        pointBackgroundColor: 'rgb(74, 144, 226)',
                        pointBorderWidth: 2,
                        pointBorderColor: 'white',
                        order: 1,
                        tension: 0.1,
                    }
                ]
            };
        }
        
        // Find op_inflows, op_outflows, financing, and ending_cash nodes
        const inflowsNode = cashflowComponent.view.children.find(n => n.id === 'op_inflows');
        const outflowsNode = cashflowComponent.view.children.find(n => n.id === 'op_outflows');
        const financingNode = cashflowComponent.view.children.find(n => n.id === 'financing');
        const endingCashNode = cashflowComponent.view.children.find(n => n.id === 'ending_cash');
        
        if (!inflowsNode?.values || !outflowsNode?.values || !endingCashNode?.values) {
            const labels = visiblePeriods?.map(p => formatPeriodLabel(p, granularity)) || [];
            return {
                labels,
                datasets: [
                    {
                        label: 'Cash Inflows',
                        data: Array(labels.length).fill(0),
                        backgroundColor: Array(labels.length).fill('rgba(17, 140, 79, 0.4)'),
                        borderColor: 'transparent',
                        borderWidth: 0,
                        order: 2,
                        type: 'bar',
                    },
                    {
                        label: 'Cash Outflows',
                        data: Array(labels.length).fill(0),
                        backgroundColor: Array(labels.length).fill('rgba(255, 99, 99, 0.4)'),
                        borderColor: 'transparent',
                        borderWidth: 0,
                        order: 2,
                        type: 'bar',
                    },
                    {
                        label: 'Ending Cash Balance',
                        data: Array(labels.length).fill(0),
                        backgroundColor: 'transparent',
                        borderColor: 'rgb(74, 144, 226)',
                        borderWidth: 2,
                        type: 'line',
                        pointRadius: 4,
                        pointBackgroundColor: 'rgb(74, 144, 226)',
                        pointBorderWidth: 2,
                        pointBorderColor: 'white',
                        order: 1,
                        tension: 0.1,
                    }
                ]
            };
        }
        
        // Get visible periods
        const labels = visiblePeriods.map(p => formatPeriodLabel(p, granularity));
        
        // Extract values for visible periods, folding financing into inflows/outflows
        const inflowsData = visiblePeriods.map(p => {
            const opInflow = inflowsNode.values[p] || 0;
            const fin = financingNode?.values?.[p] || 0;
            return opInflow + Math.max(0, fin);
        });
        const outflowsData = visiblePeriods.map(p => {
            const opOutflow = Math.abs(outflowsNode.values[p] || 0);
            const fin = financingNode?.values?.[p] || 0;
            return opOutflow + Math.abs(Math.min(0, fin));
        });
        const cashBalanceData = visiblePeriods.map(p => endingCashNode.values[p] || 0);
        
        // Create color arrays based on whether period is historical or forecast
        const inflowsBackgroundColors = visiblePeriods.map((_, index) => 
            index < actualPeriodCount 
                ? 'rgba(17, 140, 79, 1)' // Historical: full opacity
                : 'rgba(17, 140, 79, 0.4)' // Forecast: 0.4 opacity
        );
        
        const outflowsBackgroundColors = visiblePeriods.map((_, index) => 
            index < actualPeriodCount 
                ? 'rgba(255, 99, 99, 1)' // Historical: full opacity
                : 'rgba(255, 99, 99, 0.4)' // Forecast: 0.4 opacity
        );

        const endingCashBackgroundColors = visiblePeriods.map((_, index) => 
            index < actualPeriodCount 
                ? 'rgba(70, 99, 145, 1)' // Historical: full opacity
                : 'rgba(70, 99, 145, 0.5)' // Forecast: 0.4 opacity
        );
        
        return {
            labels,
            datasets: [
                {
                    type: 'bar',
                    label: 'Cash Inflows',
                    data: inflowsData,
                    backgroundColor: inflowsBackgroundColors,
                    borderColor: 'rgba(17, 140, 79, 1)',
                    borderWidth: 1,
                    borderRadius: 8, // Rounded bars
                    borderSkipped: false,
                    yAxisID: 'y'
                },
                {
                    type: 'bar',
                    label: 'Cash Outflows',
                    data: outflowsData,
                    backgroundColor: outflowsBackgroundColors,
                    borderColor: 'rgba(255, 99, 99, 1)',
                    borderWidth: 1,
                    borderRadius: 8, // Rounded bars
                    borderSkipped: false,
                    yAxisID: 'y'
                },
                {
                    type: 'line',
                    label: 'Ending Cash Balance',
                    data: cashBalanceData,
                    borderColor: 'rgba(70, 99, 145, 1)',
                    borderWidth: 2,
                    fill: false,
                    yAxisID: 'y1',
                    tension: 0.4,
                    pointRadius: 5,
                    pointHoverRadius: 7,
                    pointBackgroundColor: endingCashBackgroundColors,
                    pointBorderColor: '#fff',
                    pointBorderWidth: 2,
                    pointHoverBackgroundColor: endingCashBackgroundColors,
                    pointHoverBorderColor: '#fff',
                    pointHoverBorderWidth: 2,
                    segment: {
                        borderColor: ctx => {
                            // Color the line segment based on whether we're in historical or forecast period
                            const index = ctx.p0DataIndex;
                            return index < actualPeriodCount - 1
                                ? 'rgba(70, 99, 145, 1)'  // Historical: full opacity
                                : 'rgba(70, 99, 145, 0.5)'; // Forecast: 0.4 opacity
                        }
                    }
                }
            ]
        };
    }, [components, visiblePeriods, granularity, actualPeriodCount]);

    // Compute an outlier-aware max for the primary bar axis.
    // If any value is > 3× the 75th percentile we cap the axis there so the
    // normal bars regain visual detail. The outlier bar renders clipped (full
    // height) which still signals that it's large.
    const yBounds = useMemo(() => {
        const inflowsDataset = chartData.datasets.find(d => d.label === 'Cash Inflows');
        const outflowsDataset = chartData.datasets.find(d => d.label === 'Cash Outflows');

        const allValues = [
            ...(inflowsDataset?.data || []),
            ...(outflowsDataset?.data || [])
        ].filter(v => v != null && v !== 0 && Number.isFinite(v));

        if (allValues.length === 0) return { max: undefined };

        const maxVal = Math.max(...allValues);
        const sorted = [...allValues].sort((a, b) => a - b);
        const p75 = sorted[Math.floor(sorted.length * 0.75)] ?? sorted[sorted.length - 1];

        if (p75 > 0 && maxVal > p75 * 3) {
            return { max: Math.ceil(p75 * 3) };
        }

        return { max: undefined };
    }, [chartData]);

    // Compute tight min/max for the secondary (cash balance) axis
    const y1Bounds = useMemo(() => {
        const cashDataset = chartData.datasets.find(d => d.label === 'Ending Cash Balance');
        const values = cashDataset?.data?.filter(v => v !== 0 && v != null) || [];
        if (values.length === 0) return { min: undefined, max: undefined };

        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const range = maxVal - minVal;
        const padding = range > 0 ? range * 0.5 : Math.abs(maxVal) * 0.1 || 1000;

        return {
            min: minVal - padding,
            max: maxVal + padding,
        };
    }, [chartData]);
    
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
            mode: 'index',
            intersect: false,
        },
        // Clip datasets at the chart area so outlier bars don't bleed outside
        clip: { left: false, top: 0, right: false, bottom: false },
        layout: {
            padding: 0
        },
        elements: {
            bar: {
                barPercentage: 0.4,
                categoryPercentage: 0.4,
            },
            line: {
                borderWidth: 2
            },
            point: {
                borderWidth: 0
            }
        },
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            datalabels: {
                display: false, // Disable any data labels on the chart
            },
            tooltip: {
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                padding: 12,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        if (context.parsed.y !== null) {
                            label += new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD',
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                            }).format(context.parsed.y);
                        }
                        return label;
                    }
                }
            }
        },
        scales: {
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                offset: false,
                beginAtZero: true,
                ...(yBounds.max !== undefined ? { max: yBounds.max } : {}),
                // Force the y-axis width to match the label column width so the plot area
                // starts exactly at the W1 boundary
                afterFit: (scaleInstance) => {
                    // Chart.js will compute its own width; override to our fixed value
                    // so the chartArea.left aligns with the unified grid period columns
                    // eslint-disable-next-line no-param-reassign
                    scaleInstance.width = 0;
                },
                title: {
                    display: false,
                },
                ticks: {
                    display: false, // Hide tick labels
                    padding: 0,
                    callback: function(value) {
                        return formatCompactCurrency(value);
                    }
                },
                grid: {
                    display: true, // Keep grid lines
                    drawBorder: false, // Hide axis border
                    drawTicks: false,
                    offset: false,
                    color: 'rgba(0, 0, 0, 0.06)', // Very subtle grid lines
                    lineWidth: 1
                }
            },
            y1: {
                type: 'linear',
                display: false,
                position: 'right',
                offset: false,
                beginAtZero: false,
                min: y1Bounds.min,
                max: y1Bounds.max,
                title: {
                    display: false,
                },
                grid: {
                    drawOnChartArea: false,
                    drawBorder: true,
                    drawTicks: false,
                    offset: false
                },
                ticks: {
                    padding: 8,
                    callback: function(value) {
                        return formatCompactCurrency(value);
                    }
                }
            },
            x: {
                display: false,
                offset: true, // Add padding so first and last bars aren't cut off
                grid: {
                    display: false,
                    drawBorder: false,
                    offset: false
                }
            }
        }
    }), [y1Bounds, yBounds]);

    // Compute y-axis ticks synchronously from bar data so CustomYAxis renders
    // on the first paint — avoids the layout flash from a useEffect + setState cycle.
    const yAxisTicks = useMemo(() => {
        const inflowsDs = chartData.datasets.find(d => d.label === 'Cash Inflows');
        const outflowsDs = chartData.datasets.find(d => d.label === 'Cash Outflows');
        const allBarValues = [
            ...(inflowsDs?.data || []),
            ...(outflowsDs?.data || []),
        ].filter(v => v != null && Number.isFinite(v));

        const dataMax = allBarValues.length > 0 ? Math.max(...allBarValues) : 0;
        const axisMax = yBounds.max !== undefined ? yBounds.max : dataMax;
        if (axisMax <= 0) return [0];

        const targetCount = 6;
        const roughStep = axisMax / (targetCount - 1);
        const mag = Math.pow(10, Math.floor(Math.log10(roughStep)));
        const residual = roughStep / mag;

        let niceStep;
        if (residual <= 1.5) niceStep = mag;
        else if (residual <= 3) niceStep = 2 * mag;
        else if (residual <= 7) niceStep = 5 * mag;
        else niceStep = 10 * mag;

        const niceMax = Math.ceil(axisMax / niceStep) * niceStep;
        const ticks = [];
        for (let v = 0; v <= niceMax; v += niceStep) {
            ticks.push(Math.round(v));
        }
        return ticks;
    }, [chartData, yBounds]);

    // Calculate metrics for legend
    const legendMetrics = useMemo(() => {
        if (!components || components.length === 0) return null;
        
        const cashflowComponent = components.find(c => c.id === 'summary');
        if (!cashflowComponent?.view?.children) return null;
        
        const inflowsNode = cashflowComponent.view.children.find(n => n.id === 'op_inflows');
        const outflowsNode = cashflowComponent.view.children.find(n => n.id === 'op_outflows');
        const financingNode = cashflowComponent.view.children.find(n => n.id === 'financing');
        const endingCashNode = cashflowComponent.view.children.find(n => n.id === 'ending_cash');
        
        if (!inflowsNode?.values || !outflowsNode?.values || !endingCashNode?.values) return null;
        
        // Current cash balance: ending cash for the last actual period (or first period if no actuals)
        const currentCashPeriodIndex = actualPeriodCount > 0 ? actualPeriodCount - 1 : 0;
        const currentCashPeriod = visiblePeriods[currentCashPeriodIndex];
        const currentCashBalance = currentCashPeriod ? (endingCashNode.values[currentCashPeriod] || 0) : 0;
        
        // Projected inflows/outflows: sum over forecast period, folding financing in
        const forecastPeriods = visiblePeriods.slice(actualPeriodCount);
        const projectedInflows = forecastPeriods.reduce((sum, period) => {
            const opInflow = inflowsNode.values[period] || 0;
            const fin = financingNode?.values?.[period] || 0;
            return sum + opInflow + Math.max(0, fin);
        }, 0);
        const projectedOutflows = forecastPeriods.reduce((sum, period) => {
            const opOutflow = Math.abs(outflowsNode.values[period] || 0);
            const fin = financingNode?.values?.[period] || 0;
            return sum + opOutflow + Math.abs(Math.min(0, fin));
        }, 0);
        
        return {
            currentCashBalance,
            projectedInflows,
            projectedOutflows
        };
    }, [components, visiblePeriods, actualPeriodCount]);

    return (
        <div className="forecast-chart-wrapper">
            <div className="chart-legend-container">
                <ChartLegend metrics={legendMetrics} />
                <CustomYAxis ticks={yAxisTicks} chartHeight={chartHeight} />
            </div>
            <div className="forecast-chart-grid" style={{ gridTemplateColumns }} >
                <div className="chart-canvas-container">
                    <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={[outlierFadePlugin]} />
                </div>
            </div>
        </div>
    );
};

export default CashFlowChart;

